import { create } from 'zustand';
import { Auth } from 'aws-amplify';

// Types
export interface User {
    username: string;
    email?: string;
    given_name?: string;
    family_name?: string;
    email_verified?: boolean;
    attributes?: Record<string, any>;
}

export interface UserState {
    user: User | null;
    isAuthenticated: boolean;
    isLoading: boolean;
    error: string | null;
    
    // Actions
    setUser: (user: User | null) => void;
    getCurrentUser: () => Promise<void>;
    updateUserAttributes: (attributes: Record<string, any>) => Promise<void>;
    refreshUser: () => Promise<void>;
    signOut: () => Promise<void>;
    clearError: () => void;
    reset: () => void;
}

// Initial state
const initialState = {
    user: null,
    isAuthenticated: false,
    isLoading: false,
    error: null,
};

// Create the store
export const useUserStore = create<UserState>((set, get) => ({
    ...initialState,

    // Actions
    setUser: (user: User | null) => {
        set({ 
            user,
            isAuthenticated: !!user,
            error: null
        });
    },

    getCurrentUser: async () => {
        try {
            set({ isLoading: true, error: null });
            
            const currentUser = await Auth.currentAuthenticatedUser();
            
            if (currentUser) {
                // Extract username from various possible locations
                const username = currentUser.username || 
                               currentUser.signInUserSession?.idToken?.payload['cognito:username'] || 
                               currentUser.signInUserSession?.idToken?.payload.sub ||
                               currentUser.signInUserSession?.idToken?.payload.email;
                
                const user: User = {
                    username,
                    email: currentUser.attributes?.email,
                    given_name: currentUser.attributes?.given_name,
                    family_name: currentUser.attributes?.family_name,
                    email_verified: currentUser.attributes?.email_verified === 'true',
                    attributes: currentUser.attributes
                };
                
                set({ 
                    user,
                    isAuthenticated: true,
                    isLoading: false 
                });
            } else {
                set({ 
                    user: null,
                    isAuthenticated: false,
                    isLoading: false 
                });
            }
        } catch (error: any) {
            console.error('Failed to get current user:', error);
            set({ 
                user: null,
                isAuthenticated: false,
                error: error.message || 'Failed to get current user',
                isLoading: false 
            });
        }
    },

    updateUserAttributes: async (attributes: Record<string, any>) => {
        try {
            set({ isLoading: true, error: null });
            
            const currentUser = await Auth.currentAuthenticatedUser();
            await Auth.updateUserAttributes(currentUser, attributes);
            
            // Refresh user data after update
            await get().getCurrentUser();
            
        } catch (error: any) {
            console.error('Failed to update user attributes:', error);
            set({ 
                error: error.message || 'Failed to update user attributes',
                isLoading: false 
            });
            throw error; // Re-throw to allow components to handle
        }
    },

    refreshUser: async () => {
        await get().getCurrentUser();
    },

    signOut: async () => {
        try {
            set({ isLoading: true, error: null });
            
            await Auth.signOut();
            
            set({ 
                user: null,
                isAuthenticated: false,
                isLoading: false 
            });
        } catch (error: any) {
            console.error('Failed to sign out:', error);
            set({ 
                error: error.message || 'Failed to sign out',
                isLoading: false 
            });
            throw error;
        }
    },

    clearError: () => {
        set({ error: null });
    },

    reset: () => {
        set(initialState);
    }
}));

// Selectors for better performance
export const useUser = () => useUserStore(state => state.user);
export const useIsAuthenticated = () => useUserStore(state => state.isAuthenticated);
export const useUserLoading = () => useUserStore(state => state.isLoading);
export const useUserError = () => useUserStore(state => state.error);
export const useUsername = () => useUserStore(state => state.user?.username);
export const useUserEmail = () => useUserStore(state => state.user?.email);
export const useUserFullName = () => useUserStore(state => {
    const user = state.user;
    if (!user) return null;
    
    const firstName = user.given_name || '';
    const lastName = user.family_name || '';
    
    if (firstName && lastName) {
        return `${firstName} ${lastName}`;
    } else if (firstName) {
        return firstName;
    } else if (lastName) {
        return lastName;
    }
    
    return user.username;
});

export default useUserStore;